import React, { useCallback } from 'react';
import { type Message } from '../AccountSearchAiChat.domain';
import styles from '../AccountSearchAiChat.mod.less';
import { type AssistantResponseRecord } from 'javascripts/api';
import SanitizeInnerHTML from 'components/SanitizeInnerHTML/SanitizeInnerHTML';
import AhaAnalytics from 'javascripts/analytics';

interface chatMessageProps {
  message: Message;
  onResultClick: OnResultClick;
  showDisclaimer: boolean;
}

type OnResultClick = (item: {
  url: string;
  record_type: string;
  drawerable: boolean;
  type: string;
}) => void;

const ChatMessage: React.FC<chatMessageProps> = ({
  message,
  onResultClick,
  showDisclaimer,
}) => {
  if (message.role === 'user') {
    return (
      <div className={styles.userChatMessage}>
        <aha-chat-bubble>{message.text}</aha-chat-bubble>
      </div>
    );
  }

  const content = message.meta.content;
  const records = message.meta.records || [];

  return (
    <div className={styles.assistantChatMessage}>
      <aha-ai-icon className='tw-pt-[2px]' />
      <div className={styles.responseBody}>
        <SanitizeInnerHTML
          html={content}
          element={<div className='tw-flex tw-flex-col tw-gap-3' />}
        />

        {records.length > 0 && (
          <RecordList records={records} onResultClick={onResultClick} />
        )}
        {showDisclaimer && <Disclaimer />}
      </div>
    </div>
  );
};

function RecordList({
  records,
  onResultClick,
}: {
  records: AssistantResponseRecord[];
  onResultClick: OnResultClick;
}) {
  const groupedRecords = records.reduce((acc, record) => {
    const typeDisplay = record.record_type_display;

    if (!acc[typeDisplay]) {
      acc[typeDisplay] = {
        records: [],
        report_url: record.report_url,
      };
    }
    acc[typeDisplay].records.push(record);
    return acc;
  }, {} as Record<string, { records: AssistantResponseRecord[]; report_url: string | null }>);

  const trackViewInReportClick = (type: string) => {
    AhaAnalytics.track(
      'Feature-7464992085252752693',
      'Global search - User views report from AI chat',
      {
        'Record type': type,
      }
    );
  };

  return (
    <div className={styles.recordList}>
      {Object.entries(groupedRecords).map(
        ([typeDisplay, { records, report_url }]) => (
          <div key={typeDisplay} className={styles.recordGroup}>
            <div className={styles.recordGroupHeader}>
              <strong>{typeDisplay}s</strong>
              {report_url && (
                <a
                  className={styles.viewInReportLink}
                  href={report_url}
                  target='_blank'
                  onClick={() => trackViewInReportClick(typeDisplay)}
                >
                  View in report
                </a>
              )}
            </div>
            <ul className={styles.recordGroupList}>
              {records.map(record => {
                const key = `${record.record_type}-${
                  record.reference_num || record.name
                }`;
                return (
                  <li key={key}>
                    <Record record={record} onClick={onResultClick} />
                  </li>
                );
              })}
            </ul>
          </div>
        )
      )}
    </div>
  );
}

function Record({
  record,
  onClick,
}: {
  record: AssistantResponseRecord;
  onClick: OnResultClick;
}) {
  const handleClick = useCallback(
    e => {
      e.preventDefault();
      onClick({
        url: record.url,
        drawerable: true,
        record_type: record.record_type,
        type: 'filter assistant result',
      });
    },
    [record, onClick]
  );

  // Tasks don't have a reference number.  We display the name in the reference number pill instead of showing the name as a separate link.
  const showLink = record.record_type !== 'Task';
  const reference =
    record.record_type === 'Task' ? record.name : record.reference_num;

  return (
    <div className={styles.recordRow}>
      <aha-record-reference
        record-type={record.record_type}
        size={'small'}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
        class='tw-flex-shrink-0'
      >
        {reference}
      </aha-record-reference>
      {showLink && (
        <a onClick={handleClick} href={record.url}>
          {record.name}
        </a>
      )}
    </div>
  );
}

function Disclaimer() {
  return (
    <em>
      These responses have been generated automatically using AI and may include
      inaccurate information.
    </em>
  );
}

export default ChatMessage;
